import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  ScrollText,
  Trash2,
} from 'lucide-react';

const OPERATIONS = [
  { value: 'all', label: 'All operations' },
  { value: 'verify_email', label: 'Verify Email' },
  { value: 'create_batch', label: 'Create Batch' },
  { value: 'batch_status', label: 'Batch Status' },
  { value: 'batch_results', label: 'Batch Results' },
  { value: 'check_credits', label: 'Check Credits' },
  { value: 'test_connection', label: 'Test Connection' },
  { value: 'brevo_get_events', label: 'Brevo: Get Events' },
];

function getStatusBadge(status: number | null) {
  if (status === null) return <Badge variant="outline">N/A</Badge>;
  if (status >= 200 && status < 300) return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{status}</Badge>;
  if (status >= 400 && status < 500) return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">{status}</Badge>;
  if (status >= 500) return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function getOperationBadge(operation: string) {
  const colors: Record<string, string> = {
    verify_email: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    create_batch: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
    batch_status: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100',
    batch_results: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-100',
    check_credits: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    test_connection: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
    brevo_get_events: 'bg-orange-100 text-orange-800 hover:bg-orange-100',
  };
  return <Badge className={colors[operation] || 'bg-gray-100 text-gray-800 hover:bg-gray-100'}>{operation}</Badge>;
}

function truncateUrl(url: string, maxLen = 60) {
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '...';
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function tryFormatJson(str: string | null): string {
  if (!str) return '';
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

export default function ApiLogs() {
  const [logs, setLogs] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [operation, setOperation] = useState('all');
  const [onlyErrors, setOnlyErrors] = useState(false);

  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const [cleaning, setCleaning] = useState(false);

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const data = await api.getApiLogs({
        operation: operation === 'all' ? undefined : operation,
        isError: onlyErrors || undefined,
        page,
      });
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
  }, [operation, onlyErrors]);

  const handleRowClick = async (logEntry: any) => {
    setSheetOpen(true);
    setDetailLoading(true);
    try {
      const data = await api.getApiLogDetail(logEntry.id);
      setSelectedLog(data.log);
    } catch {
      setSelectedLog(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      await api.cleanupApiLogs(30);
      fetchLogs(1);
    } catch {
      // ignore
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">API Logs</h2>
          <p className="text-muted-foreground">
            Request/response journal for external API calls
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleCleanup} disabled={cleaning}>
          {cleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Cleanup 30d+
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATIONS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Checkbox
                id="errors-only"
                checked={onlyErrors}
                onCheckedChange={(checked) => setOnlyErrors(checked === true)}
              />
              <label htmlFor="errors-only" className="text-sm cursor-pointer">
                Errors only
              </label>
            </div>

            <div className="ml-auto text-sm text-muted-foreground">
              {pagination.total} records
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ScrollText className="h-10 w-10 mb-3 opacity-50" />
              <p>No logs found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Time</TableHead>
                    <TableHead className="w-[140px]">Operation</TableHead>
                    <TableHead className="w-[60px]">Method</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                    <TableHead className="w-[80px] text-right">Time (ms)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(log)}
                    >
                      <TableCell className="text-xs text-muted-foreground">
                        {formatTime(log.created_at)}
                      </TableCell>
                      <TableCell>{getOperationBadge(log.operation)}</TableCell>
                      <TableCell className="font-mono text-xs">{log.method}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[300px] truncate" title={log.url}>
                        {truncateUrl(log.url)}
                      </TableCell>
                      <TableCell>{getStatusBadge(log.response_status)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {log.response_time_ms ?? '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => fetchLogs(pagination.page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => fetchLogs(pagination.page + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Log Details</SheetTitle>
            <SheetDescription>
              {selectedLog ? `${selectedLog.operation} - ${selectedLog.method} ${selectedLog.response_status ?? 'ERR'}` : 'Loading...'}
            </SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : selectedLog ? (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Operation</span>
                  <div className="mt-1">{getOperationBadge(selectedLog.operation)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">{getStatusBadge(selectedLog.response_status)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Response time</span>
                  <div className="mt-1 font-medium">{selectedLog.response_time_ms} ms</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <div className="mt-1">{new Date(selectedLog.created_at).toLocaleString('ru-RU')}</div>
                </div>
              </div>

              {selectedLog.error_message && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
                  {selectedLog.error_message}
                </div>
              )}

              <div className="text-sm">
                <span className="text-muted-foreground">URL</span>
                <div className="mt-1 font-mono text-xs break-all bg-muted rounded p-2">
                  {selectedLog.method} {selectedLog.url}
                </div>
              </div>

              <Separator />

              <Tabs defaultValue="response">
                <TabsList>
                  <TabsTrigger value="request">Request</TabsTrigger>
                  <TabsTrigger value="response">Response</TabsTrigger>
                </TabsList>
                <TabsContent value="request" className="space-y-3">
                  {selectedLog.request_headers && (
                    <div>
                      <div className="text-sm font-medium mb-1">Headers</div>
                      <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-[200px]">
                        {tryFormatJson(selectedLog.request_headers)}
                      </pre>
                    </div>
                  )}
                  {selectedLog.request_body ? (
                    <div>
                      <div className="text-sm font-medium mb-1">Body</div>
                      <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-[400px]">
                        {tryFormatJson(selectedLog.request_body)}
                      </pre>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No request body</p>
                  )}
                </TabsContent>
                <TabsContent value="response" className="space-y-3">
                  {selectedLog.response_body ? (
                    <div>
                      <div className="text-sm font-medium mb-1">Body</div>
                      <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-[500px]">
                        {tryFormatJson(selectedLog.response_body)}
                      </pre>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No response body</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
